import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import * as constants from "@/constants";
import environments from "@/environments";
import { useApi } from "@/hooks/useApi";
import { TimeStamp } from "@/models/timestamp";
import { PlotPlan, PlotPlanDetail, PlotPlanTypeType } from "@/sx-layout/components/settings/plotPlanSetting/models";

export const usePlotPlanApi = () => {
  const api = useApi();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const fetchPlotPlans = async (): Promise<GetPlotPlansResponse | undefined> => {
    const endpoint = `${environments.API_BASE_URI}/plot_plans`;
    return api.get(endpoint);
  };

  const fetchPlotPlan = async (plotPlanId: number): Promise<PlotPlanDetail | undefined> => {
    const endpoint = `${environments.API_BASE_URI}/plot_plans/${plotPlanId}`;
    return api.get(endpoint);
  };

  const createPlotPlan = async (request: CreatePlotPlanRequest): Promise<any> => {
    const endpoint = `${environments.API_BASE_URI}/plot_plans/create`;
    const formData = new FormData();
    formData.append("quality", String(request.quality));
    formData.append("plot_plan_name", String(request.plot_plan_name));
    formData.append("note", String(request.note));
    formData.append("uploadfile", request.uploadfile);
    formData.append("plot_plan_type", String(request.plot_plan_type));

    return api.post(endpoint, formData, {
      errorCallback: (error: any) => {
        dispatch({
          type: constants.APP_SHOW_ERROR,
          // backendのメッセージが正しく表示されないので上書きする
          payload: error?.errors?.[0]?.err_code === "E0028" ? { message: t("error_file_is_not_pdf") } : error,
          error: true,
        });
      },
    });
  };

  const updatePlotPlan = async (request: UpdatePlotPlanRequest, reload?: () => void): Promise<any> => {
    const { plot_plan_id, ...restInput } = request;
    const endpoint = `${environments.API_BASE_URI}/plot_plans/update/${plot_plan_id}`;
    return api.post(endpoint, restInput, { reloadCallback: () => reload?.() });
  };

  return {
    fetchPlotPlans,
    fetchPlotPlan,
    createPlotPlan,
    updatePlotPlan,
  };
};

type GetPlotPlansResponse = { plot_plans: PlotPlan[] };

type CreatePlotPlanRequest = {
  quality: number;
  plot_plan_name: string;
  note: string;
  uploadfile: File;
  plot_plan_type: PlotPlanTypeType;
};

type UpdatePlotPlanRequest = {
  plot_plan_id: number;
  quality: number;
  plot_plan_name: string;
  note: string;
  plot_plan_type: PlotPlanTypeType;
  timestamp: TimeStamp;
};
